import { Modal, Group, Button, Stack, Radio, Text } from '@mantine/core';

import { UpdateScope } from 'lib/constants/inspections';
import { getScopeComponentName } from 'lib/inspections/electricalComponents';

interface MultiModuleUpdateModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: (scope: UpdateScope) => void;
    moduleCountsByScope: {
        inverter?: number;
        combiner?: number;
        stack?: number;
    };
    // Component identifiers
    componentIds?: {
        inverterId?: string;
        combinerId?: string;
        stackId?: string;
    };
    selectedScope: UpdateScope;
    setSelectedScope: (scope: UpdateScope) => void;
}

const MultiModuleUpdateModal = ({
    opened,
    onClose,
    onConfirm,
    moduleCountsByScope,
    componentIds,
    selectedScope,
    setSelectedScope,
}: MultiModuleUpdateModalProps) => {
    // Helper function to format combiner label with both combiner and inverter IDs
    const getCombinerLabel = (combinerId?: string, inverterId?: string) => {
        if (combinerId && inverterId) {
            return ` (Combiner: ${combinerId}, Inverter: ${inverterId})`;
        } else if (combinerId) {
            return ` (${combinerId})`;
        }

        return '';
    };

    const getOptions = () => {
        const options = [];

        // Add inverter option if available
        if (moduleCountsByScope.inverter && moduleCountsByScope.inverter > 1) {
            options.push({
                value: UpdateScope.INVERTER,
                label: `${moduleCountsByScope.inverter} modules affected by the same anomaly within the same ${getScopeComponentName(UpdateScope.INVERTER)}${componentIds?.inverterId ? ` (${componentIds.inverterId})` : ''}`,
            });
        }

        // Always add stack option if available (even if inverter is available)
        if (moduleCountsByScope.stack && moduleCountsByScope.stack > 1) {
            options.push({
                value: UpdateScope.STACK,
                label: `${moduleCountsByScope.stack} modules affected by the same anomaly within the same ${getScopeComponentName(UpdateScope.STACK)}${componentIds?.stackId ? ` (${componentIds.stackId})` : ''}`,
            });
        }

        // Add combiner option if available
        if (moduleCountsByScope.combiner && moduleCountsByScope.combiner > 1) {
            options.push({
                value: UpdateScope.COMBINER,
                label:
                    `${moduleCountsByScope.combiner} modules affected by the same anomaly within the same ` +
                    `${getScopeComponentName(UpdateScope.COMBINER)}` +
                    `${getCombinerLabel(componentIds?.combinerId, componentIds?.inverterId)}`,
            });
        }

        // Always add single module option
        options.push({ value: UpdateScope.SINGLE, label: 'Selected module only' });

        return options;
    };

    const handleConfirm = () => {
        onConfirm(selectedScope);
        onClose();
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Status Update" size="md">
            <Stack spacing="md">
                <Text size="sm">Select what modules should be updated.</Text>
                <Radio.Group
                    value={selectedScope}
                    onChange={(value) => setSelectedScope(value as UpdateScope)}
                    label=""
                >
                    <Stack spacing="sm">
                        {getOptions().map((option) => (
                            <Radio key={option.value} value={option.value} label={option.label} />
                        ))}
                    </Stack>
                </Radio.Group>
                <Group position="right" mt="md" spacing="sm">
                    <Button onClick={handleConfirm}>Confirm</Button>
                    <Button variant="subtle" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default MultiModuleUpdateModal;
